import { SectionLabel } from "./WhatMakesSpecial";

const depositMethods = [
  { icon: "ri-bank-card-line", title: "Visa & Mastercard", sub: "Instant — Min. AUD 19", desc: "Deposit instantly using your Visa or Mastercard. Funds appear in your account immediately with no processing fees charged by Rolex9." },
  { icon: "ri-smartphone-line", title: "PayID", sub: "Instant — Min. AUD 19", desc: "Australia&apos;s most popular instant payment method. Link your phone number or email to your bank account and deposit in seconds — no card details required." },
  { icon: "ri-exchange-line", title: "OSKO", sub: "Instant — Min. AUD 19", desc: "Fast bank-to-bank transfers via the New Payments Platform (NPP). OSKO payments are processed in near real-time, 24 hours a day, 7 days a week." },
  { icon: "ri-apple-line", title: "Apple Pay", sub: "Instant — Min. AUD 19", desc: "Deposit securely using Apple Pay on your iPhone or Mac. Touch ID or Face ID authentication adds an extra layer of security to every transaction." },
  { icon: "ri-google-line", title: "Google Pay", sub: "Instant — Min. AUD 19", desc: "Fast and secure deposits via Google Pay on Android devices. No card details shared with Rolex9 — your payment information stays private." },
  { icon: "ri-money-dollar-circle-line", title: "AmoPay & SpeedPay", sub: "Instant — Min. AUD 19", desc: "Local Australian payment solutions offering fast, secure deposits. Popular among players who prefer dedicated gaming payment accounts." },
];

const withdrawalMethods = [
  { icon: "ri-bank-card-line", title: "Visa & Mastercard", sub: "Instant — Min. AUD 29", desc: "Withdraw back to your Visa or Mastercard with standard processing times. Reliable and straightforward for players who prefer traditional banking." },
  { icon: "ri-smartphone-line", title: "PayID", sub: "Near-instant — Min. AUD 29", desc: "The fastest withdrawal option for Australian players. PayID withdrawals are processed near-instantly via the NPP — funds arrive in your bank account within minutes." },
  { icon: "ri-exchange-line", title: "OSKO", sub: "Near-instant — Min. AUD 29", desc: "Bank-to-bank withdrawals via OSKO are processed in near real-time. Available 24/7 with no cut-off times — the preferred choice for fast cashouts." },
  { icon: "ri-building-2-line", title: "Bank Transfer", sub: "Instant — Min. AUD 29", desc: "Standard bank wire transfers for larger withdrawals. Processed within 1–3 business days with full security and no third-party involvement." },
];

function MethodCard({ icon, title, sub, desc }: { icon: string; title: string; sub: string; desc: string }) {
  return (
    <div
      className="glass-card rounded-xl p-5 flex gap-4 hover:border-[#c9a84c]/15 transition-all duration-300"
      style={{ borderColor: "rgba(255,255,255,0.05)" }}
    >
      <div
        className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
        style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.12), rgba(107,33,168,0.12))" }}
      >
        <i className={`${icon} text-[#c9a84c] text-lg`}></i>
      </div>
      <div>
        <p className="text-white/70 font-semibold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{title}</p>
        <p className="text-[#c9a84c]/50 text-xs mb-1">{sub}</p>
        <p className="text-white/30 text-xs leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="h-px w-6" style={{ background: "linear-gradient(90deg, #c9a84c, transparent)" }}></div>
      <span className="text-[#c9a84c]/60 text-xs tracking-widest uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{children}</span>
    </div>
  );
}

export default function PaymentSection() {
  return (
    <section id="payment" className="max-w-5xl mx-auto px-6 py-12">
      <SectionLabel>Payment Variants</SectionLabel>
      <p className="text-white/40 text-sm leading-relaxed mb-8 max-w-2xl">
        Rolex9 supports all major Australian payment methods — PayID, OSKO, Visa, Mastercard, Apple Pay and Google Pay. Minimum deposit is AUD 19 and minimum withdrawal is AUD 29.
      </p>

      {/* Payment logos from rolex9.net */}
      <div className="glass-card rounded-2xl p-5 mb-10">
        <div className="flex flex-wrap items-center justify-center gap-5">
          {[
            { src: "https://xt30sf.b-cdn.net/media/53b5d40061396a2f2173c.png", alt: "Visa" },
            { src: "https://xt30sf.b-cdn.net/media/45897b0061396a8921f26.png", alt: "Mastercard" },
            { src: "https://xt30sf.b-cdn.net/media/6c1f142061396b1b1d924.png", alt: "Google Pay" },
            { src: "https://xt30sf.b-cdn.net/media/8e7d5410613965b22e34a.png", alt: "Apple Pay" },
            { src: "https://xt30sf.b-cdn.net/media/66e97f20613969d994bb9.png", alt: "AmoPay" },
            { src: "https://xt30sf.b-cdn.net/media/656a0730613969324beeb.png", alt: "SpeedPay" },
            { src: "https://xt30sf.b-cdn.net/media/765dcdef5139686d734a5.png", alt: "OSKO" },
            { src: "https://xt30sf.b-cdn.net/media/08c834ef51396bb598c3a.png", alt: "PayID" },
          ].map((logo) => (
            <img
              key={logo.alt}
              src={logo.src}
              alt={logo.alt}
              className="h-8 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity"
            />
          ))}
        </div>
      </div>

      <div className="mb-8">
        <SubLabel>Deposit Methods</SubLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {depositMethods.map((m, i) => <MethodCard key={i} {...m} />)}
        </div>
      </div>

      <div>
        <SubLabel>Withdrawal Methods</SubLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {withdrawalMethods.map((m, i) => <MethodCard key={i} {...m} />)}
        </div>
      </div>
    </section>
  );
}
